import { useCallback, useEffect, useRef, useState } from "react";
import useInterval from "../../hooks/useInterval";
import OfflineBanner from "../Shared/OfflineBanner/OfflineBanner";
import styles from "./Offline.module.css";

const DEFAULT_TIMEOUT = 3000;
const DEFAULT_POLLING_INTERVAL = 5000;
const MIN_POLLING_INTERVAL = 3000;
const MAX_TIMEOUT = 10000;
const TIMEOUT_STEP = 3500;
const MAX_SUBSEQUENT_ATTEMPTS = 3;

type OfflineProps = { urls?: string[] };

const Offline = ({ urls }: OfflineProps) => {
  const [isOffline, setIsOffline] = useState(false);
  const [connectivityCheckTimeout, setConnectivityCheckTimeout] = useState(
    DEFAULT_TIMEOUT
  );

  const subsequentAttempts = useRef<number>(0);

  const [pollingInterval, setPollingInterval] = useState(
    DEFAULT_POLLING_INTERVAL
  );

  const checkConnectivity = useCallback(
    async (urls?: string[]) => {
      if (!navigator.onLine) {
        handleOffline();
        return;
      }

      const urlsToCheck =
        urls?.map((url) => timeoutRequest(url, connectivityCheckTimeout)) ?? [];

      try {
        await Promise.any(urlsToCheck);
        handleOnline();
      } catch (error) {
        handleOffline();
      }
    },
    [connectivityCheckTimeout]
  );

  useEffect(() => {
    checkConnectivity(urls);
  }, [urls, checkConnectivity]);

  useInterval(() => {
    checkConnectivity(urls);
  }, pollingInterval);

  function handleOnline(): void {
    setIsOffline(false);
    subsequentAttempts.current = 0;
    setPollingInterval(DEFAULT_POLLING_INTERVAL);
    setConnectivityCheckTimeout(DEFAULT_TIMEOUT);
  }

  function handleOffline() {
    if (subsequentAttempts.current === MAX_SUBSEQUENT_ATTEMPTS) {
      setIsOffline(true);
      return;
    }

    subsequentAttempts.current = subsequentAttempts.current + 1;

    setPollingInterval((currentPollingInterval) => {
      return currentPollingInterval > MIN_POLLING_INTERVAL
        ? MIN_POLLING_INTERVAL
        : currentPollingInterval;
    });
    setConnectivityCheckTimeout((currentConnectivityCheckTimeout) => {
      return currentConnectivityCheckTimeout < MAX_TIMEOUT
        ? currentConnectivityCheckTimeout + TIMEOUT_STEP
        : currentConnectivityCheckTimeout;
    });
  }

  return isOffline ? (
    <>
      <div
        className={`${styles.pageOverlay} min-vw-100 min-vh-100 fixed-top`}
      />
      <OfflineBanner />
    </>
  ) : null;
};

function timeoutRequest(url: string, timeoutInMs: number): Promise<Response> {
  const controller = new AbortController();
  return new Promise(async (resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("TIMEOUT"));
      controller.abort();
    }, timeoutInMs);

    try {
      const resp = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      resolve(resp);
    } catch (error) {
      clearTimeout(timer);
      reject(error);
    }
  });
}

export default Offline;
